import { useEffect, useState } from "react";
import api from "../api";

export default function YouTubeBot() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [awardPoints, setAwardPoints] = useState(5);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [viewsError, setViewsError] = useState("");
  const [items, setItems] = useState([]);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [lastAwards, setLastAwards] = useState({ awards: 0, lastRunAt: null, loading: false, error: '' });
  const [chat, setChat] = useState({ sessionId: '', endsAt: null, running: false, channels: [], status: null, result: null, loading: false, error: '' });
  const [health, setHealth] = useState({ loading: false, error: '', data: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get('/admin/live-settings');
        if (!mounted) return;
        setEnabled(!!data.youtubeBotEnabled);
        if (typeof data.youtubeLurkPoints === 'number') setAwardPoints(data.youtubeLurkPoints);
        try {
          const r2 = await api.get('/admin/youtube-bot/last-awards');
          if (mounted) setLastAwards(prev => ({ ...prev, awards: Number(r2.data?.awards||0), lastRunAt: r2.data?.finishedAt || r2.data?.lastRunAt || null }));
        } catch {}
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.error || e?.message || 'Fehler beim Laden.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleToggle = async (checked) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put('/admin/live-settings', { youtubeBotEnabled: !!checked });
      setEnabled(!!data.youtubeBotEnabled);
      setSuccess('Einstellung gespeichert.');
      setTimeout(() => setSuccess(""), 1500);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Konnte nicht speichern.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAwardPoints = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put('/admin/live-settings', { youtubeLurkPoints: Number.isFinite(Number(awardPoints)) ? parseInt(awardPoints,10) : 5 });
      setAwardPoints(typeof data.youtubeLurkPoints === 'number' ? data.youtubeLurkPoints : 5);
      setSuccess('Punktewert gespeichert.');
      setTimeout(() => setSuccess(""), 1500);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Konnte Punktewert nicht speichern.');
    } finally {
      setSaving(false);
    }
  };

  const refreshLastAwards = async () => {
    setLastAwards(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const r = await api.get('/admin/youtube-bot/last-awards');
      setLastAwards({ awards: Number(r.data?.awards||0), lastRunAt: r.data?.finishedAt || r.data?.lastRunAt || null, loading: false, error: '' });
    } catch (e) {
      setLastAwards(prev => ({ ...prev, loading: false, error: e?.response?.data?.error || e?.message || 'Fehler beim Laden.' }));
    }
  };

  const fetchActive = async () => {
    setViewsLoading(true);
    setViewsError("");
    try {
      const { data } = await api.get('/admin/youtube-bot/active-viewers');
      const list = Array.isArray(data?.entries) ? data.entries : [];
      setItems(list);
      setLastFetchedAt(new Date());
    } catch (e) {
      setViewsError(e?.response?.data?.error || e?.message || 'Fehler beim Abfragen der YouTube‑Live‑Streams.');
      setItems([]);
    } finally {
      setViewsLoading(false);
    }
  };

  const startChatListen = async (minutes = 10) => {
    setChat(prev => ({ ...prev, loading: true, error: '', result: null }));
    try {
      const { data } = await api.post('/admin/youtube-bot/listen/start', { minutes });
      setChat({ sessionId: data.sessionId, endsAt: data.endsAt ? new Date(data.endsAt) : null, running: !data.immediate, channels: data.channels || [], status: null, result: null, loading: false, error: '' });
    } catch (e) {
      setChat(prev => ({ ...prev, loading: false, error: e?.response?.data?.error || e?.message || 'Konnte Chat-Mitlesen nicht starten.' }));
    }
  };

  const refreshChatStatus = async () => {
    if (!chat.sessionId) return;
    try {
      const { data } = await api.get('/admin/youtube-bot/listen/status', { params: { sessionId: chat.sessionId } });
      setChat(prev => ({ ...prev, status: data, running: !!data.running, endsAt: data.endsAt ? new Date(data.endsAt) : prev.endsAt, channels: Array.isArray(data.channels) ? data.channels : prev.channels }));
    } catch (e) {
      setChat(prev => ({ ...prev, error: e?.response?.data?.error || e?.message || 'Status konnte nicht geladen werden.' }));
    }
  };

  const loadChatResult = async () => {
    if (!chat.sessionId) return;
    setChat(prev => ({ ...prev, loading: true }));
    try {
      const { data } = await api.get('/admin/youtube-bot/listen/result', { params: { sessionId: chat.sessionId } });
      setChat(prev => ({ ...prev, result: data, loading: false }));
    } catch (e) {
      setChat(prev => ({ ...prev, loading: false, error: e?.response?.data?.error || e?.message || 'Ergebnis konnte nicht geladen werden.' }));
    }
  };

  const loadHealth = async () => {
    setHealth({ loading: true, error: '', data: null });
    try {
      const { data } = await api.get('/admin/youtube-bot/health');
      setHealth({ loading: false, error: '', data });
    } catch (e) {
      setHealth({ loading: false, error: e?.response?.data?.error || e?.message || 'Health-Check fehlgeschlagen.', data: null });
    }
  };

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>YouTube‑Bot</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>Steuere hier die Aktivierung des YouTube‑Bots. Weitere Optionen folgen.</p>
      </section>
      <section style={{ width:'100%', maxWidth:780, margin:'16px auto 40px', padding:16 }}>
        {error && (
          <div style={{color:'#ff6b6b', background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.35)', padding:'10px 12px', borderRadius:8, marginBottom:12}}>{error}</div>
        )}
        {success && (
          <div style={{color:'#2ecc71', background:'rgba(46,204,113,0.08)', border:'1px solid rgba(46,204,113,0.35)', padding:'10px 12px', borderRadius:8, marginBottom:12}}>{success}</div>
        )}
        <div style={{display:'flex', alignItems:'center', gap:16, padding:16, background:'rgba(0,0,0,0.08)', border:'1px solid #222', borderRadius:12}}>
          <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer'}}>
            <input type="checkbox" disabled={loading||saving} checked={!!enabled} onChange={e => handleToggle(e.target.checked)} />
            <span style={{fontWeight:700}}>YouTube‑Bot aktivieren</span>
          </label>
          {loading && <span style={{opacity:.7}}>Lädt …</span>}
          {saving && <span style={{opacity:.7}}>Speichert …</span>}
          <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:8}}>
            <span style={{fontSize:13, opacity:.9}}>Lurk‑Punkte pro Match:</span>
            <input
              type="number"
              min={0}
              value={awardPoints}
              onChange={e => setAwardPoints(e.target.value)}
              style={{width:80, padding:'6px 8px', borderRadius:6, border:'1px solid #333', background:'#0b0f14', color:'#dfe7ef'}}
            />
            <button onClick={handleSaveAwardPoints} disabled={saving} style={{padding:'6px 10px', border:'1px solid #345', background:'#245', color:'#fff', borderRadius:6}}>Speichern</button>
          </div>
        </div>
        <div style={{marginTop:14, fontSize:14, color:'#cde7ff'}}>
          Hinweis: Diese Einstellung ist global. Der Bot reagiert nur, wenn die YouTube‑API und OAuth korrekt eingerichtet sind.
        </div>

        <hr style={{borderColor:'#333', margin:'24px 0'}} />
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <button
            onClick={fetchActive}
            disabled={!enabled || viewsLoading || loading || saving}
            style={{
              opacity: (!enabled || viewsLoading || loading || saving) ? 0.6 : 1,
              cursor: (!enabled || viewsLoading || loading || saving) ? 'not-allowed' : 'pointer',
              padding:'10px 14px', borderRadius:8, border:'1px solid #2b5', background:'#184', color:'#fff', fontWeight:700
            }}
          >
            {viewsLoading ? 'Frage YouTube‑Live ab …' : 'Aktuelle YouTube‑Live‑Streams anzeigen'}
          </button>
          <div title={lastAwards.lastRunAt ? `Letzter Lauf: ${new Date(lastAwards.lastRunAt).toLocaleString()}` : 'Noch keine Daten'} style={{fontSize:12, padding:'4px 8px', border:'1px solid #234', background:'#0c1620', color:'#9fd', borderRadius:999}}>
            Letzte Vergaben: <b>+{Number(lastAwards.awards||0)}</b>
          </div>
          <button onClick={refreshLastAwards} disabled={lastAwards.loading} style={{padding:'6px 10px', border:'1px solid #334', background:'#123', color:'#9fd', borderRadius:6}}>
            {lastAwards.loading ? 'Lädt…' : 'Aktualisieren'}
          </button>
          {!enabled && <span style={{fontSize:13, color:'#ffc'}}>Aktiviere zuerst den YouTube‑Bot, um diese Aktion zu nutzen.</span>}
        </div>

        {viewsError && (
          <div style={{marginTop:12, color:'#ff6b6b', background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.35)', padding:'10px 12px', borderRadius:8}}>
            {viewsError}
          </div>
        )}

        {items.length > 0 && (
          <div style={{marginTop:16}}>
            <div style={{marginBottom:8, color:'#9fd', fontSize:14}}>
              Ergebnisse{lastFetchedAt ? ` — Stand: ${lastFetchedAt.toLocaleTimeString()}` : ''}
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr auto', gap:8, border:'1px solid #222', borderRadius:8}}>
              {items.map(item => {
                const key = `${item.entryId}-${item.youtubeChannelId || item.streamLink}`;
                const isExpanded = !!expanded[key];
                const names = Array.isArray(item.chatters) ? item.chatters : [];
                const shown = isExpanded ? names : names.slice(0, 50);
                return (
                <div key={key} style={{display:'contents'}}>
                  <div style={{padding:'10px 12px', borderBottom:'1px solid #222'}}>
                    <div style={{fontWeight:700}}>{item.userDisplayName || 'Kanal'}</div>
                    <div style={{fontSize:13, opacity:.8}}>
                      <a href={item.streamLink} target="_blank" rel="noreferrer" style={{color:'#8fd'}}>
                        YouTube‑Kanal
                      </a>
                      {item.streamTitle ? <> — <span title="Stream‑Titel">{item.streamTitle}</span></> : null}
                    </div>
                    {(shown.length > 0 || (item.chattersTotal||0) > 0) ? (
                      <div style={{marginTop:8, fontSize:13, color:'#cde7ff'}}>
                        Chat‑Autoren ({isExpanded ? names.length : Math.min(names.length, 50)}):{' '}
                        {shown.join(', ')}
                        {!isExpanded && names.length > 50 ? (
                          <>
                            {' '}…{' '}
                            <button
                              onClick={() => setExpanded(prev => ({ ...prev, [key]: true }))}
                              style={{ background:'transparent', color:'#9fd', border:'none', textDecoration:'underline', cursor:'pointer' }}
                            >
                              Alle anzeigen
                            </button>
                          </>
                        ) : null}
                        {isExpanded && names.length > 50 ? (
                          <>
                            {' '}
                            <button
                              onClick={() => setExpanded(prev => ({ ...prev, [key]: false }))}
                              style={{ background:'transparent', color:'#9fd', border:'none', textDecoration:'underline', cursor:'pointer' }}
                            >
                              Weniger anzeigen
                            </button>
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <div style={{marginTop:8, fontSize:13, color:'#b9c6d0', opacity:.85}}>
                        Keine Chat‑Autoren verfügbar (YouTube‑OAuth des Broadcasters erforderlich).
                      </div>
                    )}
                    {Array.isArray(item.matchedMembers) && item.matchedMembers.length > 0 ? (
                      <div style={{marginTop:8, fontSize:13, color:'#cde7ff'}}>
                        Zugeordnete Mitglieder ({item.matchedMembers.length}):{' '}
                        {item.matchedMembers.map((m, idx) => (
                          <span key={`${m.userId}-${idx}`}>
                            <a href={`/u/${m.userId}`} target="_blank" rel="noreferrer" style={{color:'#9fd'}}>{m.displayName || m.username}</a>
                            {m.chatter ? ` (${m.chatter})` : ''}
                            {m.matchedBy ? ` – Quelle: ${m.matchedBy === 'youtubeName' ? 'Profil (YouTube)' : 'DisplayName'}` : ''}
                            {idx < item.matchedMembers.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div style={{padding:'10px 12px', textAlign:'right', borderBottom:'1px solid #222', fontWeight:700}}>
                    {typeof item.viewerCount === 'number' ? `${item.viewerCount} Zuschauer` : '—'}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        <hr style={{borderColor:'#333', margin:'24px 0'}} />
        <div>
          <div style={{display:'flex', alignItems:'center', gap:12}}>
            <button
              onClick={() => startChatListen(10)}
              disabled={!enabled || chat.loading || loading || saving}
              style={{
                opacity: (!enabled || chat.loading || loading || saving) ? 0.6 : 1,
                cursor: (!enabled || chat.loading || loading || saving) ? 'not-allowed' : 'pointer',
                padding:'10px 14px', borderRadius:8, border:'1px solid #2b5', background:'#184', color:'#fff', fontWeight:700
              }}
            >
              {chat.loading ? 'Startet …' : 'YouTube‑Chat‑Mitlesen starten (10 min)'}
            </button>
            {chat.sessionId && (
              <div style={{fontSize:12, padding:'4px 8px', border:'1px solid #234', background:'#0c1620', color:'#9fd', borderRadius:999}}>
                Session: {chat.sessionId} — Kanäle: {(chat.channels||[]).length} — Ende: {chat.endsAt ? chat.endsAt.toLocaleTimeString() : '—'}
              </div>
            )}
            <button onClick={refreshChatStatus} disabled={!chat.sessionId} style={{padding:'6px 10px', border:'1px solid #334', background:'#123', color:'#9fd', borderRadius:6}}>
              Status aktualisieren
            </button>
            <button onClick={loadChatResult} disabled={!chat.sessionId || (chat.status && chat.status.running)} style={{padding:'6px 10px', border:'1px solid #334', background:'#123', color:'#9fd', borderRadius:6}}>
              Ergebnis laden
            </button>
          </div>
          {chat.error && (
            <div style={{marginTop:12, color:'#ff6b6b', background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.35)', padding:'10px 12px', borderRadius:8}}>
              {chat.error}
            </div>
          )}
          {chat.status && (
            <div style={{marginTop:12, fontSize:13, color:'#cde7ff'}}>
              Läuft: {chat.status.running ? 'Ja' : 'Nein'} — Kanäle: {(chat.status.channels||[]).length} — Endet: {chat.status.endsAt ? new Date(chat.status.endsAt).toLocaleTimeString() : '—'}
            </div>
          )}
          {chat.result && (
            <div style={{marginTop:16}}>
              <div style={{marginBottom:8, color:'#9fd', fontSize:14}}>Ergebnis YouTube‑Chat</div>
              <div style={{border:'1px solid #222', borderRadius:8}}>
                {(Array.isArray(chat.result.resultByChannel) ? chat.result.resultByChannel : []).map((rc, idx) => (
                  <div key={`rc-${idx}`} style={{padding:'10px 12px', borderBottom:'1px solid #222'}}>
                    <div style={{fontWeight:700}}>Channel: {rc.channelId}</div>
                    <div style={{fontSize:13, opacity:.85}}>Top Chatters:</div>
                    <div style={{fontSize:13}}>
                      {(rc.result||[]).slice(0,20).map((row, j) => (
                        <div key={`row-${idx}-${j}`}>{row.displayName || row.handle} — Nachrichten: {row.messages}, Wörter: {row.words}</div>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{padding:'10px 12px', fontWeight:700}}>Vergebene Awards: {chat.result.awards?.total || 0}</div>
              </div>
            </div>
          )}
          <div style={{marginTop:16}}>
            <button onClick={loadHealth} disabled={health.loading} style={{padding:'8px 12px', border:'1px solid #334', background:'#123', color:'#9fd', borderRadius:6}}>
              {health.loading ? 'Prüfe …' : 'Health‑Check ausführen'}
            </button>
            {health.error && (
              <div style={{marginTop:8, color:'#ff6b6b', background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.35)', padding:'10px 12px', borderRadius:8}}>
                {health.error}
              </div>
            )}
            {health.data && (
              <div style={{marginTop:8, padding:'10px 12px', border:'1px solid #223', background:'#0a121a', color:'#cde7ff', borderRadius:8}}>
                <div style={{marginBottom:6}}>
                  Bot aktiviert: <b>{health.data.botEnabled ? 'Ja' : 'Nein'}</b> — API Key: <b>{health.data.apiKeyPresent ? 'vorhanden' : 'fehlt'}</b>{health.data.apiKeyMasked ? ` (${health.data.apiKeyMasked})` : ''}
                </div>
                <div style={{marginBottom:6}}>
                  Einträge: <b>{health.data.summary?.totalEntries || 0}</b> — OAuth ok: <b>{health.data.summary?.withOAuth || 0}</b> — OAuth fehlt: <b>{health.data.summary?.missingOAuth || 0}</b> — Aktive Live‑Chats: <b>{health.data.summary?.activeLiveChats || 0}</b>
                </div>
                <div style={{fontSize:13, opacity:.9}}>
                  {(Array.isArray(health.data.entries) ? health.data.entries : []).slice(0,10).map((e, i) => (
                    <div key={`h-${e.entryId}-${i}`} style={{marginTop:4}}>
                      {e.userDisplayName || 'Kanal'} — OAuth: {e.hasOAuth ? 'ok' : 'fehlt'} — LiveChat: {e.activeLiveChatId ? 'aktiv' : '—'} {e.streamTitle ? `— "${e.streamTitle}"` : ''}
                    </div>
                  ))}
                  {(Array.isArray(health.data.entries) && health.data.entries.length > 10) ? (
                    <div style={{marginTop:4, opacity:.8}}>… {health.data.entries.length - 10} weitere</div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
